import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { comics, categories } from '@/mocks/manga';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'guoman');
  const [sortBy, setSortBy] = useState('update');
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  const currentCategory = categories.find(c => c.slug === activeCategory);
  const mainCategories = categories.slice(0, 3);

  let filteredComics = comics.filter(c => {
    if (activeCategory === 'lianzai') return c.status === '连载';
    if (activeCategory === 'wanjie') return c.status === '完结';
    return c.category === activeCategory || c.subCategory === activeCategory;
  });

  if (activeTab !== 'all') {
    filteredComics = filteredComics.filter(c => c.subCategory === activeTab || c.tags.includes(activeTab === 'rexue' ? '热血' : activeTab === 'lianai' ? '恋爱' : ''));
  }

  if (sortBy === 'rating') {
    filteredComics = [...filteredComics].sort((a, b) => b.rating - a.rating);
  } else if (sortBy === 'views') {
    filteredComics = [...filteredComics].sort((a, b) => b.views - a.views);
  } else {
    filteredComics = [...filteredComics].sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());
  }

  const relatedCategories = categories.filter(c => !mainCategories.find(mc => mc.slug === c.slug));

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory?.name || '漫画'} - 古古漫画网`,
    description: currentCategory?.description || '古古漫画网漫画分类',
    url: `https://www.gugumanga.com/category/${activeCategory}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '古古漫画网',
      url: 'https://www.gugumanga.com',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(categorySchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
            <Breadcrumb items={[
              { label: '首页', href: '/' },
              { label: currentCategory?.name || '分类' },
            ]} />

            <div className="mb-8">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                {currentCategory?.name || '漫画分类'}
              </h1>
              {currentCategory && (
                <p className="text-sm text-foreground-500 mt-2">{currentCategory.description}</p>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-8">
              <span className="text-sm font-medium text-foreground-600 mr-1">地区：</span>
              {mainCategories.map(cat => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 text-sm rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 border border-background-200/70'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}

              <span className="text-sm font-medium text-foreground-600 ml-4 mr-1">更多：</span>
              {relatedCategories.map(cat => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-3 py-2 text-sm rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 border border-background-200/70'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-1.5 bg-background-100 rounded-full p-1">
                {[
                  { key: 'all', label: '全部' },
                  { key: 'rexue', label: '热血' },
                  { key: 'lianai', label: '恋爱' },
                  { key: 'lianzai', label: '连载中' },
                  { key: 'wanjie', label: '已完结' },
                ].map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`px-4 py-1.5 text-xs font-medium rounded-full transition-all whitespace-nowrap cursor-pointer ${
                      activeTab === tab.key
                        ? 'bg-background-50 text-foreground-900'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-500 whitespace-nowrap">排序：</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-3 py-1.5 text-xs rounded-lg border border-background-200 bg-background-50 text-foreground-700 focus:outline-none focus:border-primary-400 cursor-pointer"
                >
                  <option value="update">最近更新</option>
                  <option value="rating">评分最高</option>
                  <option value="views">人气最热</option>
                </select>
              </div>
            </div>

            {filteredComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5 mb-12">
                {filteredComics.map(comic => (
                  <Link
                    key={comic.id}
                    to={`/article/${comic.id}`}
                    className="group cursor-pointer"
                    data-product-shop
                  >
                    <div className="relative overflow-hidden rounded-lg aspect-[3/4] mb-3">
                      <img
                        src={comic.cover}
                        alt={`${comic.title} - 古古漫画网`}
                        title={`${comic.title} - ${comic.tags.join(' ')}`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                      />
                      <div className="absolute top-2 left-2">
                        <span className={`px-2 py-0.5 rounded-md text-xs font-medium ${
                          comic.status === '连载' ? 'bg-accent-500/90 text-background-50' : 'bg-secondary-500/90 text-background-50'
                        }`}>
                          {comic.status}
                        </span>
                      </div>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                      {comic.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-1">
                      {comic.author} · {comic.rating}分
                    </p>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">暂无该分类的漫画，换个分类试试吧</p>
              </div>
            )}

            <div className="text-center">
              <button className="px-8 py-3 bg-background-100 hover:bg-background-200 text-foreground-700 font-medium rounded-lg transition-all whitespace-nowrap cursor-pointer text-sm">
                加载更多
              </button>
            </div>
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}